import { useEffect, useState } from 'react'
import { Printer } from 'lucide-react'
import { desktop } from '@/lib/printTransport'
import { isBhooklyNativeServerApp } from '@/lib/bhooklyNative'
import { getReceiptPrinter, setReceiptPrinter } from '@/lib/stationPrinterMap'
import { TestPrinterButton } from '@/components/admin/TestPrinterButton'

/**
 * Picks the physical printer for customer receipts / Z-reports / credit notes /
 * PRA invoices (the "counter" printer). Machine-local, like the kitchen-station
 * printer mapping — printers are physical to this terminal. Empty => the
 * machine's OS default printer (the historical behaviour).
 *
 * Two shells, two kinds of value (F-FE-N105):
 *
 *   • Desktop — a Windows printer NAME, picked from the installed list.
 *   • Server APK — an `ip:port`, because the tablet talks to the printer over
 *     the network itself. It cannot enumerate anything, so it is typed.
 *
 * Both live in the same machine-local key, which is safe because a device is
 * only ever one of the two. Before this the component returned null on
 * anything that was not the desktop, so a tablet acting as a till had no way to
 * say where its receipts should go.
 *
 * Still hidden in a plain browser: there the receipt follows the OS print
 * dialog and there is nothing to configure.
 */
export function ReceiptPrinterSetting() {
  const isDesktop = !!desktop()
  const isTablet = isBhooklyNativeServerApp()
  const [printers, setPrinters] = useState<{ name: string; isDefault: boolean }[] | null>(null)
  const [value, setValue] = useState<string>('')

  useEffect(() => {
    if (!isDesktop && !isTablet) return
    setValue(getReceiptPrinter())
    const d = desktop()
    if (!d) return
    let alive = true
    d.getPrinters()
      .then((list) => alive && setPrinters(list))
      .catch(() => alive && setPrinters([]))
    return () => {
      alive = false
    }
  }, [isDesktop, isTablet])

  if (!isDesktop && !isTablet) return null

  const onChange = (v: string) => {
    setValue(v)
    setReceiptPrinter(v)
  }

  return (
    <div className="rounded-lg border border-border p-4">
      <label className="text-sm font-medium text-foreground flex items-center gap-2 mb-1">
        <Printer className="w-4 h-4" /> Receipt / counter printer
      </label>
      <p className="text-xs text-muted-foreground mb-2">
        Where customer receipts, Z-reports, credit notes and tax invoices print on this terminal.
        {isTablet ? (
          <>
            {' '}
            This tablet prints over the network, so enter the printer&apos;s address —{' '}
            <strong>192.168.1.50</strong>, or <strong>192.168.1.50:9100</strong> for a non-standard
            port. Leave blank to use the printer set in the app&apos;s own settings.
          </>
        ) : (
          <>
            {' '}Leave as <strong>Default printer</strong> to use the computer&apos;s default.
          </>
        )}
      </p>
      {printers && printers.length > 0 ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Default printer</option>
          {printers.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name}
              {p.isDefault ? ' (default)' : ''}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isTablet ? '192.168.1.50:9100' : 'Default printer'}
          inputMode={isTablet ? 'numeric' : undefined}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
      )}
      {/*
        F-AND-N1: prove THIS address, not the agent's default. The only other
        test button in the POS reaches the agent's fallback IP and says so, so
        until now the value in this very box could not be checked at all.
      */}
      <TestPrinterButton target={value} label="Receipt" />
    </div>
  )
}
